import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Button,
  Box,
  Typography,
  MenuItem,
  Select,
  FormControl,
  FormHelperText,
  FormControlLabel,
  Checkbox,
  TextField,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useState } from "react";

interface Props {
  open: boolean;
  onClose: () => void;
  prefill: {
    name: string;
    surname: string;
    email: string;
    phone: string;
  };
  onSubmit: (data: {
    businessName: string;
    organisationCategory: string;
    agreeTerms: boolean;
  }) => Promise<void>;
}

const categories = [
  { label: "Funeral Parlour", value: "funeral_parlor" },
  {
    label: "Church / Religious Organisation",
    value: "church/religious_organization",
  },
  { label: "Community Organisation", value: "community_organization" },
  { label: "Insurance Company", value: "insurance_company" },
  { label: "Individual Consultant", value: "individual_consultant" },
  { label: "Other", value: "other" },
];

export default function AddReferralPartnerModal({
  open,
  onClose,
  prefill,
  onSubmit,
}: Props) {
  const [businessName, setBusinessName] = useState("");
  const [organisationCategory, setOrganisationCategory] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validate = () => {
    const e: Record<string, string> = {};
    if (!businessName.trim()) e.businessName = "Business name is required";
    if (!organisationCategory) e.organisationCategory = "Select a category";
    if (!agreeTerms) e.agreeTerms = "You must agree to the terms";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      await onSubmit({ businessName, organisationCategory, agreeTerms });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: "16px" } }}
    >
      <DialogTitle sx={{ pr: 6 }}>
        Add as Referral Partner
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          {/* Read-only prefilled info */}
          <Box sx={{ p: 1.5, bgcolor: "action.hover", borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Registering
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {prefill.name} {prefill.surname}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {prefill.email}
            </Typography>
          </Box>

          {/* Business name */}
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Business / Consultant Name{" "}
              <Box component="span" sx={{ color: "error.main" }}>
                *
              </Box>
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Your funeral home or brand"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              error={!!errors.businessName}
              helperText={errors.businessName}
            />
          </Box>

          {/* Category */}
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Organisation Category{" "}
              <Box component="span" sx={{ color: "error.main" }}>
                *
              </Box>
            </Typography>
            <FormControl
              fullWidth
              size="small"
              error={!!errors.organisationCategory}
            >
              <Select
                displayEmpty
                value={organisationCategory}
                onChange={(e) => setOrganisationCategory(e.target.value)}
                renderValue={(v) =>
                  v ? (
                    categories.find((c) => c.value === v)?.label
                  ) : (
                    <Typography sx={{ color: "text.disabled" }}>
                      Select category
                    </Typography>
                  )
                }
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.organisationCategory && (
                <FormHelperText>{errors.organisationCategory}</FormHelperText>
              )}
            </FormControl>
          </Box>

          {/* Terms */}
          <FormControlLabel
            control={
              <Checkbox
                checked={agreeTerms}
                onChange={(e) => setAgreeTerms(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                I agree to the partnership terms and privacy policy
              </Typography>
            }
          />
          {errors.agreeTerms && (
            <FormHelperText error sx={{ mt: -1.5 }}>
              {errors.agreeTerms}
            </FormHelperText>
          )}

          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleSubmit}
            disabled={isSubmitting}
            sx={{ borderRadius: "24px", fontWeight: 600 }}
          >
            {isSubmitting ? (
              <CircularProgress size={20} sx={{ mr: 1 }} />
            ) : null}
            {isSubmitting ? "Adding..." : "Add as Partner"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
